import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { toast } from 'sonner';
import './index.css';
import 'leaflet/dist/leaflet.css';

import { routeTree } from '@/routeTree.gen';
import '@/translations/config';
import { AuthProvider } from '@/context/AuthContext';
import { config } from '@/config/config';
import i18n from 'i18next';

const sentryEnv =
  process.env.CF_PAGES_BRANCH && process.env.CF_PAGES_BRANCH !== 'undefined'
    ? process.env.CF_PAGES_BRANCH
    : 'local';

if (sentryEnv !== 'local') {
  Sentry.init({
    dsn: 'https://cd134448d83c6fe0a03af928ad2a852e@o4509038337916928.ingest.de.sentry.io/4511064685084752',
    environment: sentryEnv,
    release: process.env.SENTRY_RELEASE,
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Top-level error boundary so a single uncaught error doesn't result in a white screen
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 24,
            fontFamily: 'system-ui, sans-serif',
            maxWidth: 560,
            margin: '40px auto',
          }}
        >
          <h1 style={{ fontSize: 18, marginBottom: 12 }}>
            Something went wrong
          </h1>
          <p style={{ color: '#666', marginBottom: 16 }}>
            The Petso dashboard couldn&apos;t load. This has been logged. Try
            refreshing the page.
          </p>
          <pre
            style={{
              padding: 12,
              background: '#f5f5f5',
              borderRadius: 8,
              fontSize: 12,
              overflow: 'auto',
            }}
          >
            {this.state.error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16,
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      console.error('Query error:', error);
      toast.error(
        `Query Error: ${error instanceof Error ? error.message : 'An error occurred'}`
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error('Mutation error:', error);
      toast.error(
        `Mutation Error: ${error instanceof Error ? error.message : 'An error occurred'}`
      );
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Set initial dir and lang attributes
const initialLang = i18n.language || 'en';
const isRTL = initialLang === 'ar';
document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
document.documentElement.setAttribute('lang', initialLang);

// Guard: Clerk requires a non-empty publishable key; missing key can cause white screen
const clerkPubKey =
  (config?.clerk?.publishableKey?.trim?.() as string) || undefined;

function AppContent() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Show a bootstrap error in the DOM (no React) so we never stay on a white screen
function showBootstrapError(message: string, detail?: string) {
  const existing = document.getElementById('root');
  const container = existing ?? document.createElement('div');
  container.id = 'root';
  if (!existing) document.body.appendChild(container);
  container.innerHTML = `
    <div style="padding:24px;font-family:system-ui,sans-serif;max-width:560px;margin:40px auto;">
      <h1 style="font-size:18px;margin-bottom:12px;">Petso couldn't load</h1>
      <p style="color:#666;margin-bottom:16px;">${escapeHtml(message)}</p>
      ${detail ? `<pre style="padding:12px;background:#f5f5f5;border-radius:8px;font-size:12px;overflow:auto;white-space:pre-wrap;">${escapeHtml(detail)}</pre>` : ''}
      <button type="button" onclick="window.location.reload()" style="margin-top:16px;padding:8px 16px;cursor:pointer;">Refresh</button>
    </div>
  `;
}

function escapeHtml(s: string) {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

let rootEl = document.getElementById('root');
if (!rootEl) {
  rootEl = document.createElement('div');
  rootEl.id = 'root';
  document.body.appendChild(rootEl);
}

// Stable portal container so portals (e.g. NotificationSidePanel) don't append directly to body
// and cause "insertBefore" DOM errors when React's tree and DOM get out of sync.
if (!document.getElementById('portal-root')) {
  const portalRoot = document.createElement('div');
  portalRoot.id = 'portal-root';
  document.body.appendChild(portalRoot);
}

try {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        {clerkPubKey ? (
          <ClerkProvider publishableKey={clerkPubKey}>
            <AppContent />
          </ClerkProvider>
        ) : (
          <div
            style={{
              padding: 24,
              fontFamily: 'system-ui, sans-serif',
              maxWidth: 560,
              margin: '40px auto',
            }}
          >
            <h1 style={{ fontSize: 18, marginBottom: 12 }}>
              Configuration error
            </h1>
            <p style={{ color: '#666' }}>
              Clerk publishable key is not set. Set it in config for the app to
              load.
            </p>
          </div>
        )}
      </AppErrorBoundary>
    </React.StrictMode>
  );
} catch (err) {
  const message = err instanceof Error ? err.message : String(err);
  const detail = err instanceof Error ? err.stack : undefined;
  showBootstrapError(message, detail);
}

// Catch any unhandled errors that happen after load (e.g. in async code)
window.addEventListener('error', (event) => {
  if (document.body && !document.getElementById('root')?.textContent) {
    showBootstrapError(
      event.message || 'An error occurred',
      event.error?.stack
    );
  }
});

// Avoid "Uncaught (in promise) undefined" from router/auth/async edge cases
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason === undefined) {
    event.preventDefault();
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('Suppressed unhandled promise rejection (undefined). This can occur with router redirects.');
    }
  }
});
